import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Live filters demo: England map thins as a results bar rises.
 * Auto-plays on view; parent can scrub the slider.
 */

const TOTAL = 164;
const PINS_BASE = "/icons/Pins";

/** Pins on England basemap z9 (zoomed in; Ireland / Europe cropped out). */
const PINS = [
  { x: 66.4, y: 76.5, floor: 82, gender: "Boys" },
  { x: 65.1, y: 79.2, floor: 78, gender: "Girls" },
  { x: 63.0, y: 77.0, floor: 76, gender: "Mixed" },
  { x: 57.5, y: 76.4, floor: 72, gender: "Boys" },
  { x: 50.3, y: 77.5, floor: 70, gender: "Girls" },
  { x: 82.2, y: 75.8, floor: 55, gender: "Mixed" },
  { x: 71.0, y: 63.1, floor: 74, gender: "Boys" },
  { x: 44.8, y: 71.8, floor: 68, gender: "Girls" },
  { x: 93.4, y: 54.8, floor: 48, gender: "Mixed" },
  { x: 32.7, y: 57.7, floor: 80, gender: "Boys" },
  { x: 29.3, y: 68.9, floor: 65, gender: "Girls" },
  { x: 19.6, y: 77.5, floor: 60, gender: "Mixed" },
  { x: 5.0, y: 88.0, floor: 42, gender: "Boys" },
  { x: 26.2, y: 38.0, floor: 75, gender: "Girls" },
  { x: 12.0, y: 39.6, floor: 58, gender: "Mixed" },
  { x: 40.8, y: 40.0, floor: 62, gender: "Boys" },
  { x: 39.3, y: 31.7, floor: 70, gender: "Girls" },
  { x: 38.2, y: 12.0, floor: 66, gender: "Mixed" },
  { x: 69.0, y: 74.0, floor: 38, gender: "Boys" },
  { x: 59.0, y: 74.5, floor: 34, gender: "Girls" },
  { x: 54.0, y: 70.0, floor: 30, gender: "Mixed" },
  { x: 75.0, y: 70.0, floor: 28, gender: "Boys" },
  { x: 46.0, y: 62.0, floor: 24, gender: "Girls" },
  { x: 34.0, y: 50.0, floor: 22, gender: "Mixed" },
  { x: 22.0, y: 48.0, floor: 18, gender: "Boys" },
  { x: 41.0, y: 48.0, floor: 16, gender: "Girls" },
  { x: 73.0, y: 58.0, floor: 14, gender: "Mixed" },
  { x: 16.0, y: 70.0, floor: 12, gender: "Boys" },
];

const FILL = {
  Boys: "Boys_Fill_New.svg",
  Girls: "Girls_Fill.svg",
  Mixed: "Mixed_Fill.svg",
};

function countVisible(bar) {
  if (bar <= 0) return TOTAL;
  const kept = PINS.filter((p) => p.floor >= bar).length;
  const ratio = kept / PINS.length;
  return Math.max(kept, Math.round(12 + ratio * (TOTAL - 12)));
}

function displayBar(v) {
  return v <= 0 ? "Any" : `${v}+`;
}

function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

function SchoolPin({ pin, visible }) {
  const fill = FILL[pin.gender] || FILL.Mixed;
  return (
    <span
      className={`hf-pin${visible ? " is-on" : " is-off"}`}
      style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
      aria-hidden="true"
    >
      <span className="hf-pin-stack">
        <img className="hf-pin-body" src={`${PINS_BASE}/Pin_Green.svg`} alt="" draggable={false} />
        <img className="hf-pin-fill" src={`${PINS_BASE}/${fill}`} alt="" draggable={false} />
        <img className="hf-pin-child" src={`${PINS_BASE}/Secondary_child.svg`} alt="" draggable={false} />
        <img className="hf-pin-badge" src={`${PINS_BASE}/Badge_G.svg`} alt="" draggable={false} />
      </span>
    </span>
  );
}

export default function HomeFiltersStage() {
  const rootRef = useRef(null);
  const [bar, setBar] = useState(0);
  const [userScrub, setUserScrub] = useState(false);
  const userScrubRef = useRef(false);
  const resumeTimer = useRef(0);

  const shown = useMemo(() => countVisible(bar), [bar]);

  useEffect(() => {
    userScrubRef.current = userScrub;
  }, [userScrub]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setBar(70);
      return undefined;
    }

    let raf = 0;
    let start = 0;
    let playing = false;
    const CYCLE = 5200;

    const tick = (now) => {
      if (!playing || userScrubRef.current) {
        raf = requestAnimationFrame(tick);
        return;
      }
      if (!start) start = now;
      const t = ((now - start) % CYCLE) / CYCLE;
      let v = 0;
      if (t < 0.18) v = 0;
      else if (t < 0.55) v = easeInOut((t - 0.18) / 0.37) * 75;
      else if (t < 0.72) v = 75;
      else v = 75 * (1 - easeInOut((t - 0.72) / 0.28));
      setBar(Math.round(v / 5) * 5);
      raf = requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          playing = entry.isIntersecting;
          if (playing && !userScrubRef.current) start = 0;
        });
      },
      { threshold: 0.35 }
    );
    io.observe(root);
    raf = requestAnimationFrame(tick);

    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
      window.clearTimeout(resumeTimer.current);
    };
  }, []);

  const onScrub = (next) => {
    setUserScrub(true);
    setBar(next);
    window.clearTimeout(resumeTimer.current);
    resumeTimer.current = window.setTimeout(() => {
      setUserScrub(false);
    }, 2800);
  };

  return (
    <div className="hf-stage" ref={rootRef}>
      <div className="hf-map" aria-hidden="true">
        <img
          className="hf-map-photo"
          src="/home-clips/england-filters-bg.jpg?v=4"
          alt=""
          draggable={false}
        />
        <div className="hf-map-fade" />
        {PINS.map((pin, i) => (
          <SchoolPin key={i} pin={pin} visible={bar <= 0 || pin.floor >= bar} />
        ))}
      </div>

      <aside className="hf-drawer" aria-label="Filter schools preview">
        <header className="hf-drawer-head">
          <div>
            <p className="hf-drawer-title">Filter Schools</p>
            <p className="hf-drawer-sub">
              Showing <strong>{shown}</strong> of {TOTAL}
            </p>
          </div>
          <span className="hf-drawer-close" aria-hidden="true">
            ✕
          </span>
        </header>

        <div className="hf-drawer-body">
          <p className="hf-sec">School profile</p>

          <div className="hf-checks" aria-hidden="true">
            <span className="hf-check is-on">Boys</span>
            <span className="hf-check is-on">Girls</span>
            <span className="hf-check">Mixed</span>
          </div>

          <div className="hf-select" aria-hidden="true">
            <span>Catchment Type</span>
            <em>Open to All</em>
          </div>
          <div className="hf-select" aria-hidden="true">
            <span>Ofsted Rating</span>
            <em>Outstanding</em>
          </div>

          <p className="hf-sec">GCSE results</p>
          <label className="hf-field">
            <span className="hf-field-label">Attainment 8</span>
            <span className="hf-field-row">
              <input
                type="range"
                className="hf-range"
                min={0}
                max={90}
                step={5}
                value={bar}
                onChange={(e) => onScrub(Number(e.target.value))}
                aria-valuetext={displayBar(bar)}
                aria-label="Attainment 8 filter"
              />
              <em className="hf-field-val">{displayBar(bar)}</em>
            </span>
          </label>
          <div className="hf-field hf-field--muted" aria-hidden="true">
            <span className="hf-field-label">Grades 8–9 %</span>
            <span className="hf-field-row">
              <span className="hf-range-ghost" />
              <em className="hf-field-val">35%+</em>
            </span>
          </div>

          <p className="hf-sec">11+ exam subjects</p>
          <div className="hf-checks" aria-hidden="true">
            <span className="hf-check is-on">English</span>
            <span className="hf-check is-on">Maths</span>
            <span className="hf-check">Verbal</span>
            <span className="hf-check">Non-verbal</span>
          </div>
        </div>
      </aside>

      <div className="hf-caption">
        <p>Set the filters you care about — schools that don’t match leave the map.</p>
      </div>
      <span className="hf-map-credit">Map © OpenStreetMap</span>
    </div>
  );
}
